import { useState, useEffect, useRef } from 'react';
import {
  Box, Popover, Typography, IconButton, Tooltip, Badge, List, ListItemButton,
  Avatar, TextField, InputAdornment, CircularProgress, Divider, alpha, useTheme,
} from '@mui/material';
import {
  Inbox as InboxIcon, Send as SendIcon, ArrowBack as BackIcon,
  OpenInFull as OpenIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { messagingService, CustomerConversation, CustomerMessage } from '../../services/messaging.service';
import { useAuth } from '../../hooks/useAuth';

function timeAgo(iso?: string) {
  if (!iso) return '';
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'now';
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}

export default function InboxPopover() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { currentOrganization } = useAuth();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [convos, setConvos] = useState<CustomerConversation[]>([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState<CustomerConversation | null>(null);
  const [messages, setMessages] = useState<CustomerMessage[]>([]);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const orgId = currentOrganization?.id;
  const totalUnread = convos.reduce((n, c) => n + (c.unread_count || 0), 0);

  const load = async () => {
    if (!orgId) return;
    setLoading(true);
    try { setConvos(await messagingService.listConversations(orgId, {})); }
    finally { setLoading(false); }
  };

  // Poll for the unread badge even when closed; live-subscribe while open
  useEffect(() => { load(); }, [orgId]);
  useEffect(() => {
    if (!orgId) return;
    const unsub = messagingService.subscribeToInbox(orgId, () => load());
    return () => { try { unsub?.(); } catch {} };
  }, [orgId]);

  const open = (e: React.MouseEvent<HTMLElement>) => { setAnchorEl(e.currentTarget); load(); };
  const close = () => { setAnchorEl(null); setActive(null); setMessages([]); setReply(''); };

  const openConvo = async (c: CustomerConversation) => {
    setActive(c);
    setMessages(await messagingService.getMessages(c.id));
    if (c.unread_count) { messagingService.markAsRead(c.id).then(load); }
    setTimeout(() => scrollRef.current?.scrollTo({ top: 9e9 }), 50);
  };

  const send = async () => {
    if (!reply.trim() || !active || !orgId) return;
    setSending(true);
    try {
      await messagingService.sendMessage(active.customer_id as string, reply.trim(), { phone: active.phone_number });
      setReply('');
      setMessages(await messagingService.getMessages(active.id));
      setTimeout(() => scrollRef.current?.scrollTo({ top: 9e9 }), 50);
    } finally { setSending(false); }
  };

  const isOpen = Boolean(anchorEl);

  return (
    <>
      <Tooltip title="Messages" enterDelay={0}>
        <IconButton
          onClick={open}
          sx={{
            color: isOpen ? 'primary.main' : 'text.primary',
            '&:hover': { bgcolor: theme.palette.mode === 'dark' ? alpha('#fff', 0.12) : alpha('#000', 0.08) },
          }}
        >
          <Badge
            badgeContent={totalUnread}
            color="error"
            max={99}
            overlap="circular"
            sx={{ '& .MuiBadge-badge': { fontSize: 10, height: 16, minWidth: 16, padding: '0 4px' } }}
          >
            <InboxIcon fontSize="small" />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { width: 380, maxWidth: '95vw', height: 480, mt: 1, borderRadius: 2, display: 'flex', flexDirection: 'column', overflow: 'hidden' } }}
      >
        {/* Header */}
        <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `1px solid ${theme.palette.divider}` }}>
          {active ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
              <IconButton size="small" onClick={() => { setActive(null); setMessages([]); }}><BackIcon fontSize="small" /></IconButton>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle2" noWrap>{(active as any).customer ? `${(active as any).customer.first_name} ${(active as any).customer.last_name}` : active.phone_number}</Typography>
                <Typography variant="caption" color="text.secondary">{active.phone_number}</Typography>
              </Box>
            </Box>
          ) : (
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Inbox{totalUnread ? ` · ${totalUnread}` : ''}</Typography>
          )}
          <Tooltip title="Open full inbox">
            <IconButton size="small" onClick={() => { navigate('/inbox'); close(); }}><OpenIcon fontSize="small" /></IconButton>
          </Tooltip>
        </Box>

        {/* Body */}
        {!active ? (
          <Box sx={{ flex: 1, overflowY: 'auto' }}>
            {loading && !convos.length ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>
            ) : !convos.length ? (
              <Box sx={{ textAlign: 'center', py: 5, px: 3, color: 'text.secondary' }}>
                <InboxIcon sx={{ fontSize: 36, opacity: 0.4, mb: 1 }} />
                <Typography variant="body2">No messages yet.</Typography>
                <Typography variant="caption">Website "Text us" enquiries land here.</Typography>
              </Box>
            ) : (
              <List disablePadding>
                {convos.map(c => {
                  const nm = (c as any).customer ? `${(c as any).customer.first_name} ${(c as any).customer.last_name}` : c.phone_number;
                  return (
                    <ListItemButton key={c.id} onClick={() => openConvo(c)} sx={{ px: 2, py: 1.25, gap: 1.5, alignItems: 'flex-start' }}>
                      <Badge color="error" variant="dot" invisible={!c.unread_count} overlap="circular">
                        <Avatar sx={{ width: 36, height: 36, fontSize: 14, bgcolor: alpha(theme.palette.primary.main, 0.15), color: 'primary.main' }}>
                          {(nm || '?').slice(0, 1).toUpperCase()}
                        </Avatar>
                      </Badge>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                          <Typography variant="body2" noWrap sx={{ fontWeight: c.unread_count ? 700 : 500 }}>{nm}</Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>{timeAgo(c.last_message_at)}</Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', fontWeight: c.unread_count ? 600 : 400 }}>
                          {c.last_message_preview || '—'}
                        </Typography>
                      </Box>
                    </ListItemButton>
                  );
                })}
              </List>
            )}
          </Box>
        ) : (
          <>
            <Box ref={scrollRef} sx={{ flex: 1, overflowY: 'auto', px: 2, py: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
              {messages.map(m => (
                <Box key={m.id} sx={{ alignSelf: m.direction === 'outbound' ? 'flex-end' : 'flex-start', maxWidth: '80%' }}>
                  <Box sx={{ px: 1.5, py: 1, borderRadius: 2, bgcolor: m.direction === 'outbound' ? 'primary.main' : alpha(theme.palette.text.primary, 0.08), color: m.direction === 'outbound' ? 'primary.contrastText' : 'text.primary' }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{m.body}</Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: m.direction === 'outbound' ? 'right' : 'left', mt: 0.25 }}>{timeAgo(m.created_at)}</Typography>
                </Box>
              ))}
            </Box>
            <Divider />
            <Box sx={{ p: 1.5, display: 'flex', gap: 1, alignItems: 'flex-end' }}>
              <TextField
                fullWidth size="small" multiline maxRows={3} placeholder="Reply by SMS…"
                value={reply} onChange={e => setReply(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                InputProps={{ endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" color="primary" onClick={send} disabled={sending || !reply.trim()}>
                      {sending ? <CircularProgress size={18} /> : <SendIcon fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                )}}
              />
            </Box>
          </>
        )}
      </Popover>
    </>
  );
}
